import React, { useRef, useEffect, useState, useCallback } from 'react';
import { X, Camera } from 'lucide-react';
import { faceApi } from '../services/api';

interface FaceEnrollmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  employeeId: number;
  onSuccess: (imageBase64: string) => void;
}

const FaceEnrollmentModal: React.FC<FaceEnrollmentModalProps> = ({
  isOpen,
  onClose,
  employeeId,
  onSuccess,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [pendingImageBase64, setPendingImageBase64] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const startCamera = useCallback(async () => {
    if (!isOpen) return;
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280, min: 640 }, height: { ideal: 720, min: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err: any) {
      console.error('Camera error:', err);
      setCameraError(err?.message || 'Could not access camera. Please allow camera permissions.');
    }
  }, [isOpen]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      startCamera();
    }
    return () => {
      stopCamera();
    };
  }, [isOpen, startCamera, stopCamera]);

  const resetCapture = () => {
    setCapturedImage(null);
    setPendingImageBase64(null);
    setError(null);
  };

  const handleClose = () => {
    resetCapture();
    stopCamera();
    onClose();
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !streamRef.current) {
      setError('Camera not ready');
      return;
    }
    setError(null);
    setIsProcessing(true);
    try {
      await new Promise(r => setTimeout(r, 150));
      const video = videoRef.current;
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      if (!vw || !vh) {
        setError('Camera not ready. Please wait a moment and try again.');
        return;
      }
      const size = Math.min(vw, vh);
      const sx = (vw - size) / 2;
      const sy = (vh - size) / 2;
      const canvas = document.createElement('canvas');
      canvas.width = 640;
      canvas.height = 640;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context not available');
      ctx.drawImage(video, sx, sy, size, size, 0, 0, 640, 640);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
      const base64 = dataUrl.split(',')[1] || dataUrl;
      setCapturedImage(dataUrl);
      setPendingImageBase64(base64);
    } catch (err: any) {
      setError(err?.message || 'Failed to capture photo');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEnroll = async () => {
    if (!pendingImageBase64) {
      setError('Please capture a photo first');
      return;
    }
    setIsProcessing(true);
    setError(null);
    try {
      const result = await faceApi.enroll({
        employeeId,
        imageBase64: pendingImageBase64,
      });
      if (!result.success) {
        throw new Error(result.message || 'Failed to enroll face');
      }
      onSuccess(pendingImageBase64);
      handleClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to enroll face');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex justify-between items-center px-6 py-4 border-b border-neutral-200">
          <h2 className="text-lg font-semibold text-neutral-800">Face Enrollment</h2>
          <button onClick={handleClose} className="text-neutral-400 hover:text-neutral-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-neutral-600">
            You need to enroll your face before using My Attendances. Position your face in the frame and capture a clear photo.
          </p>

          {!capturedImage ? (
            <div className="relative aspect-square bg-neutral-900 rounded-lg overflow-hidden">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
                style={{ transform: 'scaleX(-1)' }}
              />
              {cameraError && (
                <div className="absolute inset-0 flex items-center justify-center bg-neutral-800 text-white text-center p-4">
                  {cameraError}
                </div>
              )}
              <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                <button
                  onClick={capturePhoto}
                  disabled={isProcessing || !!cameraError}
                  className="flex items-center gap-2 px-6 py-3 bg-white rounded-full shadow-lg hover:bg-neutral-100 disabled:opacity-50"
                >
                  <Camera className="h-6 w-6 text-neutral-700" />
                  <span className="font-medium">Capture Photo</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative aspect-square rounded-lg overflow-hidden border-2 border-neutral-200">
                <img src={capturedImage} alt="Captured" className="w-full h-full object-cover" style={{ transform: 'scaleX(-1)' }} />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={resetCapture}
                  disabled={isProcessing}
                  className="flex-1 px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
                >
                  Retake
                </button>
                <button
                  onClick={handleEnroll}
                  disabled={isProcessing}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {isProcessing ? 'Enrolling...' : 'Enroll Face'}
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="px-3 py-2 bg-red-50 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FaceEnrollmentModal;
